import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import type { AdminApiBootResponse } from "@repo/app-types/api/boot";
import * as Sentry from "@sentry/react";
import { posthog } from "posthog-js";
import type { RootState } from "../app/store.js";
import { setCompany } from "../features/company/companySlice.js";
import { setFeatureFlagKeys } from "../features/featureFlags/featureFlagsSlice.js";
import { setFeatureFlagVariants } from "../features/featureFlags/featureFlagVariantsSlice.js";
import { setAllIntegrations } from "../features/integrations/integrationsSlice.js";
import { setAllKeywordTrackers } from "../features/keywordTrackers/keywordTrackersSlice.js";
import { setTeammateRole } from "../features/miscellaneous/authorizationSlice.js";
import { setAllPhoneNumbers } from "../features/phoneNumbers/phoneNumbersSlice.js";
import { endServiceAccess } from "../features/serviceAccess/serviceAccessClient.js";
import { checkIsServiceMode } from "../features/serviceAccess/serviceAccessMode.js";
import { setTeammate } from "../features/teammate/teammateSlice.js";
import { getSupabaseClient } from "./supabaseClient.js";

/**
 * Thunk action creator that applies a fully-resolved boot payload to the Redux
 * store and, in production builds or with VITE_POSTHOG_ENABLED, initializes
 * PostHog. Dispatch it as `dispatch(applyBootState(data))` once `GET /user`
 * reports the session is `resolved` and `GET /boot` returns.
 * (Account resolution itself is driven by the `user` RTK Query endpoint.)
 */
export function applyBootState(
  data: AdminApiBootResponse,
): ThunkAction<void, RootState, void, UnknownAction> {
  return (dispatch) => {
    const {
      teammate,
      company,
      keyword_trackers,
      integrations,
      teammate_role,
      feature_flags,
      feature_flag_variants,
    } = data;

    dispatch(setTeammate(teammate));
    if (company) {
      dispatch(setCompany(company));
    }
    dispatch(setAllKeywordTrackers(keyword_trackers));
    dispatch(setAllIntegrations(integrations));
    dispatch(setAllPhoneNumbers(data.phone_numbers));
    dispatch(setTeammateRole(teammate_role));

    dispatch(setFeatureFlagKeys(feature_flags));
    dispatch(setFeatureFlagVariants(feature_flag_variants));

    Sentry.setUser({
      id: teammate.id,
      company_id: company?.id,
    });

    // Only production builds load PostHog, unless VITE_POSTHOG_ENABLED (with
    // VITE_POSTHOG_KEY) is set for testing; otherwise its capture and reset
    // are no-ops.
    if (!import.meta.env.PROD && !import.meta.env.VITE_POSTHOG_ENABLED) {
      return;
    }

    const hasSensitiveData = Boolean(company?.has_sensitive_data);

    // An employee's service access is not the customer's usage: nothing is
    // captured, identified or recorded.
    const isServiceMode = checkIsServiceMode();
    /** Whether to do any capturing at all. */
    const canCapture =
      !isServiceMode &&
      (import.meta.env.PROD || import.meta.env.VITE_POSTHOG_ENABLED);
    /** Whether to do automatic capturing and session recording (beyond just page views and manually captured events). */
    const canAutoCapture = canCapture && !hasSensitiveData;

    posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
      api_host: "https://us.i.posthog.com",
      opt_out_capturing_by_default: !canCapture,
      autocapture: canAutoCapture,
      disable_session_recording: !canAutoCapture,
      // We read flags from /boot, so skip the POST /flags evaluation posthog-js
      // would otherwise make on load, on identify and on its refresh interval.
      // Not `advanced_disable_flags`, which would also stop the remote config
      // that autocapture and session recording are configured from.
      advanced_disable_feature_flags: true,
    });
    if (isServiceMode) {
      return;
    }
    posthog.identify(teammate.id, {
      ...(company ? { company_id: company.id } : {}),
    });

    // Create a PostHog group analytics for the company. It automatically
    // associates all subsequent events in the session (via "capture") with that
    // group.
    if (company) {
      // This will do nothing if the group already exists
      posthog.group("company", company.id, {
        name: company.name,
        created_at: company.created_at,
      });
    }
  };
}

export const signoutUser = async () => {
  if (checkIsServiceMode()) {
    await endServiceAccess();
    return;
  }
  await getSupabaseClient().auth.signOut();
  Sentry.setUser(null);
  posthog.reset();
  window.location.href = "/";
};
